/* istanbul ignore file */
import React, { useEffect, useState } from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';

import { Divider } from 'src/components/atoms/Divider/Divider';
import { ProgressBar } from 'src/components/atoms/ProgressBar/ProgressBar';
import { SkeletonProductList } from 'src/components/organisms/ProductList/components/SkeletonProductList';
import { ProductListContainer } from 'src/components/organisms/ProductList/containers/ProductListContainer';
import { PAGINATION_WRAPPER_ID } from 'src/components/organisms/ProductPages/constants/elementIds';
import { PRODUCTS_GRID_WRAPPER } from 'src/constants/elementIds';
import type { UpdateHistoryPayload } from 'src/redux/products/thunks/updatePageHistory';
import type { Product } from 'src/redux/products/types/Product';
import { getPageNumbers } from 'src/redux/products/utils/pageNumbers';
import { getProductCountUpToEndOfPage } from 'src/redux/products/utils/productCount';
import { pageHistory } from 'src/services/pageHistory';
import {
  getProductCardNodeByCode,
  scrollProductCardIntoView,
} from 'src/utils/scrollProductCardIntoView';
import { PaginationButtonLink } from './PaginationButtonLink';
import * as styles from './ProductPages.styles';

const PRODUCTS_TO_RENDER_ON_SERVER = 12;

type LastViewedProduct = Pick<UpdateHistoryPayload, 'productCode'>;

export interface ProductPagesProps {
  favouriteProductCodes: string[];
  isLoading: boolean;
  loadMoreURL: string | null;
  loadPreviousURL: string | null;
  onLoadMore: ({ page }: { page: number }) => void;
  onLoadPrevious: ({ page }: { page: number }) => void;
  pageLoading: number | null;
  productsByPage: Record<number, Product[]>;
  totalProducts: number;
}

export const ProductPages = ({
  favouriteProductCodes,
  isLoading,
  loadMoreURL,
  loadPreviousURL,
  onLoadMore,
  onLoadPrevious,
  pageLoading,
  productsByPage,
  totalProducts,
}: ProductPagesProps) => {
  const [hasRenderedRemainingProducts, setHasRenderedRemainingProducts] = useState(false);
  const pageNumbers = getPageNumbers(productsByPage);
  const lastPageDisplayed = pageNumbers.slice(-1)[0];
  const loadingPreviousPage = pageLoading && pageLoading < pageNumbers[0];
  const loadingOnlyPage = pageLoading && pageNumbers.length === 0;
  const loadingNextPage = pageLoading && pageLoading > lastPageDisplayed;

  useEffect(() => {
    const product = pageHistory!.get<LastViewedProduct>('lastViewedProduct');

    if (product) {
      scrollProductCardIntoView(getProductCardNodeByCode(product.productCode));
    }
  }, []);

  useEffect(() => {
    if (!hasRenderedRemainingProducts) {
      setHasRenderedRemainingProducts(true);
    }
  }, [hasRenderedRemainingProducts]);

  const splitProductsByIsExpandedSearch = (productsForThisPage: Product[]) => {
    return productsForThisPage.reduce(
      (result, product) => {
        result[!product.isExpandedSearch ? 0 : 1].push(product);
        return result;
      },
      [[], []] as [Product[], Product[]],
    );
  };

  return (
    <div css={styles.wrapper} id={PRODUCTS_GRID_WRAPPER}>
      <h2 css={visuallyHidden}>Products</h2>
      {!isLoading && loadPreviousURL && (
        <PaginationButtonLink
          href={loadPreviousURL}
          onClick={() => {
            onLoadPrevious({ page: pageNumbers[0] });
          }}
        >
          Load previous<span css={visuallyHidden}> products</span>
        </PaginationButtonLink>
      )}
      {loadingPreviousPage && <SkeletonProductList />}
      {loadingOnlyPage ? (
        <SkeletonProductList />
      ) : (
        pageNumbers.map((page) => {
          const productsForThisPage = productsByPage[page];
          const [products, expandedProducts] = splitProductsByIsExpandedSearch(productsForThisPage);
          const renderExpandedResultsMessage = !!expandedProducts?.length && page === 1;
          // eslint-disable-next-line no-extra-boolean-cast
          const expandedResultsMessage = !!products?.length ? (
            <Divider text="Similar items based on your search" />
          ) : (
            <h2 css={styles.expandedResultsMessage}>
              We couldn&apos;t find an exact match – but you might like these…
            </h2>
          );
          const progressCount = getProductCountUpToEndOfPage(productsByPage, page);
          const progressMessage = `Viewed ${progressCount} of ${totalProducts} products`;

          // For the first page, only render first 12 products on server, all on client
          const productsToRender =
            page === 1 && !hasRenderedRemainingProducts
              ? products.slice(0, PRODUCTS_TO_RENDER_ON_SERVER)
              : products;

          return (
            <div
              aria-hidden={isLoading}
              data-testid={`product-page-${page}`}
              key={`product-page-${page}`}
            >
              <ProductListContainer
                expandedProducts={expandedProducts}
                favouriteProductCodes={favouriteProductCodes}
                page={page}
                products={productsToRender}
                {...(renderExpandedResultsMessage && {
                  expandedResultsMessage,
                })}
              />
              <div css={[styles.progressWrapper, styles.fadeIn]} id={PAGINATION_WRAPPER_ID}>
                {lastPageDisplayed === page ? (
                  <>
                    <p css={styles.progressMessage}>{progressMessage}</p>
                    <ProgressBar part={progressCount} total={totalProducts} />
                  </>
                ) : (
                  <Divider text={progressMessage} />
                )}
              </div>
            </div>
          );
        })
      )}
      {loadingNextPage && <SkeletonProductList />}
      {!isLoading && loadMoreURL && (
        <PaginationButtonLink
          href={loadMoreURL}
          onClick={() => {
            onLoadMore({ page: lastPageDisplayed });
          }}
        >
          Load more<span css={visuallyHidden}> products</span>
        </PaginationButtonLink>
      )}
    </div>
  );
};
